/**
 * CapabilityPopover — the "?" button + popover that lists active AI tools
 * in user-language. Sits between the textarea and the ☰ library button in
 * the Composer row. Click outside to dismiss; footer link opens the
 * read-only DebugInspector drawer.
 *
 * Port of CapabilityPopover + ToolRow from
 * designs/edit-with-ai-tool-transparency-final.html — JSX is verbatim.
 * Only changes: TypeScript types, real imports, REAL_TOOLS replaced with
 * the `useSkillTools()` query, and the "primary" emphasis is derived from
 * `tool.name === 'compose_form_field'` (the design's `primary: true` flag
 * is not a server field).
 */

import { useEffect, useRef, useState } from 'react';
import { useSkillTools, type SkillTool } from './hooks/useSkillTools';

type CategoryKey = 'compose' | 'read' | 'write' | 'admin';

interface CategoryMeta {
  color: string;
  label: string;
  icon: string;
}

export const CATEGORY_META: Record<CategoryKey, CategoryMeta> = {
  compose: { color: 'blueberry', label: 'Compose', icon: '✎' },
  read: { color: 'matcha', label: 'Read', icon: '⌕' },
  write: { color: 'tangerine', label: 'Write', icon: '▶' },
  admin: { color: 'pomegranate', label: 'Admin', icon: '⚙' },
};

/** The single "primary" tool the user usually cares about most. */
const PRIMARY_TOOL_NAME = 'compose_form_field';

interface CapabilityPopoverProps {
  onOpenInspector: () => void;
  /**
   * Which edge of the "?" button the popover panel anchors to.
   *
   * `'right'` (default) anchors the panel's right edge to the button's right edge
   * (`right-0`), extending leftward — V1's `Composer.tsx` places this button near the
   * RIGHT edge of its row, so a leftward-extending panel stays on-screen. Never passed
   * explicitly at that call site; the default preserves its exact prior behavior.
   *
   * `'left'` anchors the panel's left edge to the button's left edge (`left-0`),
   * extending rightward instead — V2's `ChatComposerV2.tsx` places this button near the
   * LEFT edge of a narrow resizable dock, where the default `'right'` panel runs off the
   * left edge of the viewport.
   */
  align?: 'left' | 'right';
}

export function CapabilityPopover({ onOpenInspector, align = 'right' }: CapabilityPopoverProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data: tools } = useSkillTools();

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const list = tools ?? [];
  const primary = list.filter((t) => t.name === PRIMARY_TOOL_NAME);
  const reads = list.filter((t) => t.category === 'read');
  const writes = list.filter((t) => t.category === 'write');

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`text-xs w-9 h-9 flex items-center justify-center border rounded-md flex-shrink-0 transition-colors ${
          open
            ? 'bg-blueberry-50 border-blueberry-300 text-blueberry-700 dark:bg-blueberry-900/30 dark:border-blueberry-700 dark:text-blueberry-400'
            : 'bg-white border-nightshade-200 text-nightshade-700 hover:bg-nightshade-50 dark:bg-nightshade-900 dark:border-nightshade-700 dark:text-nightshade-300 dark:hover:bg-nightshade-800'
        }`}
        title="What can the AI do?"
        aria-label="What can the AI do?"
      >
        ?
      </button>

      {open && (
        <div
          className={`absolute bottom-full ${align === 'left' ? 'left-0' : 'right-0'} mb-2 w-80 bg-white dark:bg-nightshade-900 border border-primary dark:border-nightshade-600 rounded-xl shadow-xl z-30 animate-fadeIn overflow-hidden`}
        >
          <div className="px-3 py-2 border-b border-secondary dark:border-nightshade-700 bg-bg-secondary dark:bg-nightshade-800">
            <div className="text-xs font-semibold text-content-primary dark:text-nightshade-100">What can the AI do here?</div>
            <div className="text-[11px] text-content-tertiary dark:text-nightshade-500 mt-0.5">Active tools the assistant has access to</div>
          </div>

          <div className="max-h-[320px] overflow-auto scrollbar-thin">
            {/* Primary tool first, slightly emphasised */}
            {primary.map((t) => {
              const meta = CATEGORY_META[t.category];
              return (
                <div key={t.name} className="px-3 py-2 border-b border-secondary dark:border-nightshade-700 bg-blueberry-50/30 dark:bg-blueberry-900/20">
                  <div className="flex items-start gap-2">
                    <span
                      className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center bg-${meta.color}-50 text-${meta.color}-700 dark:bg-${meta.color}-900/30 dark:text-${meta.color}-400 text-sm`}
                    >
                      {meta.icon}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] font-semibold text-content-primary dark:text-nightshade-100 flex items-center gap-1.5">
                        {t.userLabel}
                        <span className="text-[9px] uppercase tracking-wider px-1 py-px bg-blueberry-100 text-blueberry-700 dark:bg-blueberry-900/40 dark:text-blueberry-400 rounded font-medium">
                          primary
                        </span>
                      </div>
                      <div className="text-[11px] text-content-tertiary dark:text-nightshade-500 leading-tight mt-0.5">{t.userDescription}</div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Secondary tools, grouped */}
            {reads.length > 0 && (
              <>
                <div className="px-3 pt-2 pb-1 text-[9px] uppercase tracking-wider text-content-tertiary dark:text-nightshade-500 font-medium">
                  Read
                </div>
                {reads.map((t) => (
                  <ToolRow key={t.name} tool={t} />
                ))}
              </>
            )}
            {writes.length > 0 && (
              <>
                <div className="px-3 pt-2 pb-1 text-[9px] uppercase tracking-wider text-content-tertiary dark:text-nightshade-500 font-medium">
                  Write
                </div>
                {writes.map((t) => (
                  <ToolRow key={t.name} tool={t} />
                ))}
              </>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onOpenInspector();
            }}
            className="w-full px-3 py-2 border-t border-secondary dark:border-nightshade-700 bg-bg-secondary dark:bg-nightshade-800 hover:bg-nightshade-100 dark:hover:bg-nightshade-700 text-[11px] font-medium text-content-secondary dark:text-nightshade-400 flex items-center justify-center gap-1.5 transition-colors"
          >
            <span className="font-mono text-[10px]">{'{ }'}</span>
            <span>Inspect system prompt and context</span>
            <span>→</span>
          </button>
        </div>
      )}
    </div>
  );
}

interface ToolRowProps {
  tool: SkillTool;
}

function ToolRow({ tool }: ToolRowProps) {
  const meta = CATEGORY_META[tool.category];
  return (
    <div className="px-3 py-1.5 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 transition-colors">
      <div className="flex items-start gap-2">
        <span
          className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center bg-${meta.color}-50 text-${meta.color}-700 dark:bg-${meta.color}-900/30 dark:text-${meta.color}-400 text-[11px] mt-0.5`}
        >
          {meta.icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-medium text-content-primary dark:text-nightshade-100">{tool.userLabel}</div>
          <div className="text-[10px] text-content-tertiary dark:text-nightshade-500 leading-tight">{tool.userDescription}</div>
        </div>
      </div>
    </div>
  );
}

